import { useState, useEffect, useCallback } from 'react';
import api from '../api/api';
import { calcularEdadDetallada } from '../utils/dateUtils';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

export const useRecetasPacienteLogic = ({ id_paciente, id_cita }) => {
  const [formData, setFormData] = useState({
    id_receta: '',
    fecha_receta: '',
    peso_receta: '',
    temp_receta: '',
    detalle_receta: '',
    updatedAt: null,
  });
  const [recetas, setRecetas] = useState([]);
  const [selectedReceta, setSelectedReceta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({
    fecha_receta: '',
    peso_receta: '',
    temp_receta: '',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error',
  });
  const [isMaximized, setIsMaximized] = useState(false);
  const [isVademecumOpen, setIsVademecumOpen] = useState(false);
  const [clienteNombre, setClienteNombre] = useState('');
  const [pacienteNombre, setPacienteNombre] = useState('');
  const [razaNombre, setRazaNombre] = useState('');
  const [usuarioNombre, setUsuarioNombre] = useState('');
  const [pacienteEdad, setPacienteEdad] = useState('');
  const [expandedPanel, setExpandedPanel] = useState('general');

  const normalizeReceta = useCallback((receta) => {
    if (!receta) {
      console.warn('Receta recibida es null o undefined');
      return {
        id_receta: null,
        id_paciente: id_paciente || null,
        id_cita: id_cita || null,
        id_usuario: null,
        fecha_receta: '',
        peso_receta: '',
        temp_receta: '',
        detalle_receta: '',
        createdAt: null,
        updatedAt: null,
      };
    }
    return {
      id_receta: receta.id_receta ?? null,
      id_paciente: receta.id_paciente ?? id_paciente ?? null,
      id_cita: receta.id_cita ?? id_cita ?? null,
      id_usuario: receta.id_usuario ?? null,
      fecha_receta: receta.fecha_receta ? dayjs(receta.fecha_receta).format('YYYY-MM-DD') : '',
      peso_receta: receta.peso_receta ?? '',
      temp_receta: receta.temp_receta ?? '',
      detalle_receta: receta.detalle_receta ?? '',
      createdAt: receta.createdAt ?? null,
      updatedAt: receta.updatedAt ?? null,
    };
  }, [id_paciente, id_cita]);

  const fetchDatosAdicionales = useCallback(async () => {
    if (!id_paciente) return;
    try {
      setLoading(true);
      const pacienteRes = await api.get(`/pacientes/${id_paciente}`);
      const pacienteData = pacienteRes.data;
      setPacienteNombre(pacienteData.nombre_paciente || 'N/A');
      setPacienteEdad(calcularEdadDetallada(pacienteData.fecha_nac_paciente));
      if (pacienteData.id_raza) {
        const razaRes = await api.get(`/razas/${pacienteData.id_raza}`);
        setRazaNombre(razaRes.data.nombre_raza || 'N/A');
      } else {
        setRazaNombre('N/A');
      }
      if (pacienteData.id_cliente) {
        const clienteRes = await api.get(`/clientes/${pacienteData.id_cliente}`);
        setClienteNombre(clienteRes.data.nombre_cliente || 'N/A');
      } else {
        setClienteNombre('N/A');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Error desconocido';
      console.error('Error al cargar datos de paciente/cliente:', JSON.stringify(error.response?.data || error, null, 2));
      setError(`Error al cargar datos de paciente/cliente: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  }, [id_paciente]);

  const fetchUsuarioNombre = useCallback(async (id_usuario) => {
    if (!id_usuario) {
      setUsuarioNombre('N/A');
      return;
    }
    try {
      const usuarioRes = await api.get(`/usuarios/${id_usuario}`);
      setUsuarioNombre(usuarioRes.data.nombre_usuario || 'N/A');
    } catch (error) {
      console.error('Error al cargar datos del usuario:', JSON.stringify(error.response?.data || error, null, 2));
      setUsuarioNombre('N/A');
    }
  }, []);

  const fetchRecetas = useCallback(async () => {
    if (!id_paciente) return;
    try {
      setLoading(true);
      const res = await api.get(`/recetas/?id_paciente=${id_paciente}`); // esto esta bien ahora
      const recetasData = Array.isArray(res.data) ? res.data : [];
      const sortedRecetas = recetasData.sort((a, b) => b.id_receta - a.id_receta);
      setRecetas(sortedRecetas);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Error desconocido';
      console.error('Error al cargar recetas:', JSON.stringify(error.response?.data || error, null, 2));
      setError(`Error al cargar recetas: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  }, [id_paciente]);

  const selectRecetaByCita = useCallback(async () => {
    if (!id_cita || !recetas.length) return;
    try {
      const receta = recetas.find(r => r.id_cita === id_cita);
      if (receta) {
        setFormData(normalizeReceta(receta));
        setSelectedReceta(receta);
        await fetchUsuarioNombre(receta.id_usuario);
      }
    } catch (error) {
      console.error('Error al seleccionar receta por id_cita:', error);
    }
  }, [id_cita, recetas, fetchUsuarioNombre, normalizeReceta]);

  const validateForm = () => {
    const newErrors = {
      fecha_receta: '',
      peso_receta: '',
      temp_receta: '',
    };
    let isValid = true;

    if (!formData.fecha_receta) {
      newErrors.fecha_receta = 'La fecha de la receta es obligatoria';
      isValid = false;
    }
    if (formData.peso_receta && (isNaN(formData.peso_receta) || formData.peso_receta < 0 || formData.peso_receta > 100)) {
      newErrors.peso_receta = 'El peso debe estar entre 0 y 100 Lb';
      isValid = false;
    }
    if (formData.temp_receta && (isNaN(formData.temp_receta) || formData.temp_receta < 10 || formData.temp_receta > 50)) {
      newErrors.temp_receta = 'La temperatura debe estar entre 10 y 50 °C';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSaveReceta = async () => {
  if (!validateForm()) {
    setSnackbar({
      open: true,
      message: 'Faltan datos requeridos o hay errores de formato. Por favor, revise los campos marcados.',
      severity: 'warning',
    });
    return;
  }

  try {
    const payload = {
      id_paciente: id_paciente,
      fecha_receta: formData.fecha_receta || dayjs().format('YYYY-MM-DD'),
      peso_receta: formData.peso_receta ? parseFloat(formData.peso_receta) : null,
      temp_receta: formData.temp_receta ? parseFloat(formData.temp_receta) : null,
      detalle_receta: formData.detalle_receta || null,
      id_cita: id_cita || null,
    };

    const res = await api.post('/recetas/', payload);
    const nuevaReceta = res.data; // El backend debería devolver la receta creada con su id_receta

    setSnackbar({
      open: true,
      message: 'Receta registrada exitosamente.',
      severity: 'success',
    });

    // === ACTUALIZACIÓN CORRECTA DEL ESTADO ===
    await fetchRecetas(); // Refresca el historial

    // Seleccionamos automáticamente la receta recién creada
    setSelectedReceta(nuevaReceta);
    setFormData(normalizeReceta(nuevaReceta));
    setIsEditing(false); // Ya no está en modo edición
    await fetchUsuarioNombre(nuevaReceta.id_usuario || null);

    // Opcional: cambiar a la pestaña "Receta Actual" si no está ya
    // (puedes hacerlo desde aquí o dejar que el usuario la vea en historial)

  } catch (error) {
    const errorMessage = error.response?.data?.detail || error.message || 'Error desconocido';
    console.error('Error al guardar receta:', error);
    setSnackbar({
      open: true,
      message: `Error al guardar receta: ${errorMessage}`,
      severity: 'error',
    });
  }
};

  const handleEditReceta = async () => {
  if (!validateForm()) {
    setSnackbar({
      open: true,
      message: 'Faltan datos requeridos o hay errores de formato. Por favor, revise los campos marcados.',
      severity: 'warning',
    });
    return;
  }

  try {
    if (!selectedReceta) {
      setSnackbar({
        open: true,
        message: 'Selecciona una receta válida para editar.',
        severity: 'error',
      });
      return;
    }

    const payload = {
      id_paciente: id_paciente,
      id_cita: selectedReceta.id_cita || null,
    };

    const recetaFields = ['fecha_receta', 'peso_receta', 'temp_receta', 'detalle_receta'];
    let hasChanges = false;

    recetaFields.forEach(field => {
      const currentValue = formData[field];
      const originalValue = selectedReceta[field] ?? '';

      const normalizedCurrent = field.includes('peso') || field.includes('temp')
        ? (currentValue ? parseFloat(currentValue) : null)
        : currentValue;

      const normalizedOriginal = field.includes('peso') || field.includes('temp')
        ? (originalValue ? parseFloat(originalValue) : null)
        : originalValue;

      if (normalizedCurrent !== normalizedOriginal) {
        payload[field] = field.includes('fecha')
          ? (currentValue || null)
          : normalizedCurrent;
        hasChanges = true;
      }
    });

    if (!hasChanges) {
      setIsEditing(false);
      setSnackbar({
        open: true,
        message: 'No se realizaron cambios.',
        severity: 'info',
      });
      return;
    }

    console.log('Payload enviado para edición:', JSON.stringify(payload, null, 2));
    const res = await api.put(`/recetas/${selectedReceta.id_receta}`, payload);

    // === Actualización correcta del estado local ===
    const updatedReceta = res.data;

    setSelectedReceta(updatedReceta);
    setFormData(normalizeReceta(updatedReceta));
    setIsEditing(false);
    await fetchUsuarioNombre(updatedReceta.id_usuario);

    // === ¡AQUÍ ESTÁ LA CLAVE: Refrescar el historial! ===
    await fetchRecetas();

    setSnackbar({
      open: true,
      message: 'Receta actualizada exitosamente.',
      severity: 'success',
    });

  } catch (error) {
    const errorMessage = error.response?.data?.detail || error.message || 'Error desconocido';
    console.error('Error al editar receta:', error);
    setSnackbar({
      open: true,
      message: `Error al editar receta: ${errorMessage}`,
      severity: 'error',
    });
  }
};

  const handleDeleteReceta = async (id_receta) => {
    if (window.confirm('¿Estás seguro de eliminar esta receta?')) {
      try {
        const res = await api.delete(`/recetas/${id_receta}`);
        console.log('Respuesta del servidor:', JSON.stringify(res.data, null, 2));
        setRecetas(recetas.filter(r => r.id_receta !== id_receta));
        if (selectedReceta?.id_receta === id_receta) {
          setSelectedReceta(null);
          setFormData({
            id_receta: '',
            fecha_receta: '',
            peso_receta: '',
            temp_receta: '',
            detalle_receta: '',
            updatedAt: null,
          });
          setUsuarioNombre('N/A');
        }
        setErrors({ fecha_receta: '', peso_receta: '', temp_receta: '' });
        setSnackbar({
          open: true,
          message: 'Receta eliminada exitosamente.',
          severity: 'success',
        });
      } catch (error) {
        const errorMessage = error.response?.data?.detail || error.message || 'Error desconocido';
        console.error('Error del servidor:', JSON.stringify(error.response?.data || error, null, 2));
        setSnackbar({
          open: true,
          message: `Error al eliminar receta: ${errorMessage}`,
          severity: 'error',
        });
      }
    }
  };

  const handleSelectReceta = (receta) => {
    setSelectedReceta(receta);
    setFormData(normalizeReceta(receta));
    setIsEditing(false);
    setErrors({ fecha_receta: '', peso_receta: '', temp_receta: '' });
    fetchUsuarioNombre(receta.id_usuario);
  };

  const handleNewReceta = () => {
    setSelectedReceta(null);
    setFormData({
      id_receta: '',
      fecha_receta: '',
      peso_receta: '',
      temp_receta: '',
      detalle_receta: '',
      updatedAt: null,
    });
    setUsuarioNombre('N/A');
    setIsEditing(true);
    setErrors({ fecha_receta: '', peso_receta: '', temp_receta: '' });
  };

  const handleCancelNewReceta = () => {
    setSelectedReceta(null);
    setFormData({
      id_receta: '',
      fecha_receta: '',
      peso_receta: '',
      temp_receta: '',
      detalle_receta: '',
      updatedAt: null,
    });
    setUsuarioNombre('N/A');
    setIsEditing(false);
    setErrors({ fecha_receta: '', peso_receta: '', temp_receta: '' });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  const handleChangePanel = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  const truncateText = (text, maxLength) => {
    if (!text) return 'N/A';
    return text.length > maxLength ? text.substring(0, maxLength - 3) + '...' : text;
  };

  const countWords = (text) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const countLines = (text) => {
    if (!text) return 0;
    const lines = text.split(/\r\n|\r|\n/);
    return lines.filter(line => line.trim() !== '').length;
  };

const handlePrintReceta = useCallback(async (id_receta, templateId = 1) => {
  if (!id_receta) {
    setSnackbar({ 
      open: true, 
      message: 'No hay una receta seleccionada para imprimir.', 
      severity: 'warning' 
    });
    return;
  }

  try {
    setLoading(true);
    console.info(`Abriendo receta en nueva pestaña - ID: ${id_receta}, plantilla: ${templateId}`);

    const url = `/recetas/imprimir/${id_receta}?template=${templateId}`;
    
    const response = await api.get(url, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);

    // ←←← ESTA ES LA LÍNEA CLAVE: Abre en nueva pestaña
    window.open(blobUrl, '_blank');

    // Limpieza de memoria después de unos segundos
    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
    }, 60000); // 60 segundos

    setSnackbar({ 
      open: true, 
      message: `PDF de la receta abierto en nueva pestaña (Plantilla ${templateId})`, 
      severity: 'success' 
    });

  } catch (error) {
    setLoading(false);
    const errorMessage = error.response?.data?.detail || error.message || 'Error desconocido';
    console.error('Error al abrir PDF:', error);
    
    setSnackbar({
      open: true,
      message: `Error al abrir el PDF: ${errorMessage}`,
      severity: 'error',
    });
  } finally {
    setLoading(false);
  }
}, [setSnackbar, setLoading]);

  useEffect(() => {
    fetchDatosAdicionales();
    fetchRecetas();
  }, [id_paciente, id_cita, fetchDatosAdicionales, fetchRecetas]);

  useEffect(() => {
    selectRecetaByCita();
  }, [recetas, id_cita, selectRecetaByCita]);

  return {
    formData,
    setFormData,
    recetas,
    setRecetas,
    selectedReceta,
    setSelectedReceta,
    loading,
    setLoading,
    error,
    setError,
    errors,
    setErrors,
    isEditing,
    setIsEditing,
    snackbar,
    setSnackbar,
    isMaximized,
    setIsMaximized,
    isVademecumOpen,
    setIsVademecumOpen,
    clienteNombre,
    setClienteNombre,
    pacienteNombre,
    setPacienteNombre,
    razaNombre,
    setRazaNombre,
    usuarioNombre,
    setUsuarioNombre,
    pacienteEdad,
    setPacienteEdad,
    expandedPanel,
    setExpandedPanel,
    handleSaveReceta,
    handleEditReceta,
    handleDeleteReceta,
    handleSelectReceta,
    handleNewReceta,
    handleCancelNewReceta,
    handleCloseSnackbar,
    handleChangePanel,
    truncateText,
    countWords,
    countLines,
    fetchRecetas,
    handlePrintReceta, // Agregado al objeto retornado
  };
};